import { HHStack } from '@hinge-health/react-component-library';
import { DataGridPro, GridRowId } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SubmissionStatusEnum } from '../../../../../../types';
import { useBillingWriteAccess } from '../../../../hooks/use-billing-write-access';
import { useFilterUrlSync } from '../../../../hooks/use-filter-url-sync';
import { FooterAction, GridFooter } from '../../../grid-components/grid-footer';
import { GridLevelName } from '../../../grid-components/grid-types';
import { NoRowsOverlay } from '../../../grid-components/no-rows-or-error-overlay';
import { useGridPagination } from '../../../grid-components/use-grid-pagination';
import { useGridSelection } from '../../../grid-components/use-grid-selection';
import {
  SubmissionManagementGridFilterModelType,
  SubmissionTypeData,
  SubmissionTypeDataList,
} from '../../bill-grid-components/bill-management-types';
import { useGridActions } from '../../bill-grid-components/use-grid-actions';
import { generateEnrichedData, getGridCount } from '../../utils';
import { FloatingReturnButton } from '../floating-return-button';
import SubmissionFiltersToolbar from './submission-filters-toolbar';
import { useSubmissionColumns } from './use-submission-columns';
import { useSubmissionQueries } from './use-submission-queries';

export const defaultSubmissionManagementGridFilterModel: SubmissionManagementGridFilterModelType =
  {
    submissionIds: [],
    billIds: [],
    userIds: [],
    transactionIds: [],
    netsuiteIds: [],
    creditMemoIds: [],
    submissionStatuses: [],
    submissionStartDate: null,
    submissionEndDate: null,
    clientIds: [],
    insurerIds: [],
    partnershipIds: [],
    billingTypes: [],
    source: null,
    sourceIds: [],
    sourceStartDate: null,
    sourceEndDate: null,
  };

const noneAreVoidedSubmissions = (
  submissions: SubmissionTypeDataList,
): boolean =>
  submissions.every(
    submission =>
      submission.submissionStatus !== SubmissionStatusEnum.Void.toLowerCase(),
  );

const SubmissionManagementGrid = (): JSX.Element => {
  const location = useLocation();
  const navigationState = location.state?.contractContext;
  const canWrite = useBillingWriteAccess();

  const [filterParams, setFilterParams] = useFilterUrlSync(
    defaultSubmissionManagementGridFilterModel,
  );

  const [gridRows, setGridRows] = useState<SubmissionTypeDataList>([]);

  const getSubmissionRowId = useCallback(
    (row: SubmissionTypeData): GridRowId => row.submissionId,
    [],
  );

  const {
    handleRowSelectionChange,
    clearAllSelections,
    selectedRows,
    visibleSelectedIds,
  } = useGridSelection(gridRows, getSubmissionRowId, filterParams);

  const {
    paginationModel,
    setPaginationModel,
    handlePaginationChange,
    handlePageOptionChange,
  } = useGridPagination({ page: 0, pageSize: 100 });

  const {
    ciData,
    contractsData,
    clientsData,
    insurersData,
    partnershipsData: partnershipsData,
    billingTypesData,
    submissionsData,
    isLoading,
    hasError,
    searchClientInsurerIds,
  } = useSubmissionQueries(filterParams, paginationModel);

  // Actions:
  const gridActions = useGridActions({
    clientsData,
    insurersData,
    clearAllSelections,
    gridLevel: GridLevelName.SUBMISSION,
  });

  const moreDetails = useCallback(
    (id: GridRowId) => () => {
      const row = gridRows.find(item => item.submissionId === id);
      if (row) {
        gridActions.moreDetails(row.billId);
      }
    },
    [gridRows, gridActions],
  );

  const submissionManagementColumns = useSubmissionColumns({
    clientsData,
    insurersData,
    moreDetails,
  });

  useEffect(() => {
    if (submissionsData && !hasError) {
      const rows = generateEnrichedData(
        submissionsData.items,
        ciData,
        partnershipsData,
        contractsData.items,
      );
      const ciIdsFromContracts = new Set(
        contractsData.items.map(c => c.clientsInsurerId),
      );
      if (ciIdsFromContracts.size > 0) {
        searchClientInsurerIds(ciIdsFromContracts);
      }
      setGridRows(rows);
    }
  }, [
    submissionsData,
    ciData,
    contractsData,
    partnershipsData,
    hasError,
    searchClientInsurerIds,
  ]);

  const hasSelectedRows = selectedRows.length > 0;

  const voidSubmissions = (id?: GridRowId) => () => {
    const rows =
      typeof id == 'number'
        ? (gridRows.filter(data => data.submissionId === id) ?? [])
        : selectedRows;
    gridActions.voidSubmissions(rows);
  };

  const bulkUpdateNetsuiteId = useCallback(
    (id?: GridRowId) => () => {
      const rows =
        typeof id == 'number'
          ? (gridRows.filter(data => data.submissionId === id) ?? [])
          : selectedRows;
      gridActions.bulkUpdateNetsuiteId(rows);
    },
    [gridRows, selectedRows, gridActions],
  );

  const getSubmissionActions = (): FooterAction[] =>
    !canWrite
      ? []
      : [
          {
            action: voidSubmissions(),
            actionLabel: 'Void Submissions',
            disabled:
              !hasSelectedRows || !noneAreVoidedSubmissions(selectedRows),
          },
          {
            action: bulkUpdateNetsuiteId(),
            actionLabel: 'Update Netsuite ID',
            disabled: !hasSelectedRows,
          },
        ];

  return (
    <>
      <HHStack
        flex={1}
        direction="column"
        spacing={6}
        justifyContent="space-between"
        minHeight="0"
      >
        <SubmissionFiltersToolbar
          filterParams={filterParams}
          setFilterParams={setFilterParams}
          clients={clientsData}
          insurers={insurersData}
          partnerships={partnershipsData}
          billingTypes={billingTypesData}
          setPaginationModel={setPaginationModel}
          hasError={hasError}
          isLoading={isLoading}
        />
        <HHStack direction="column" flex={1} minHeight="0">
          <DataGridPro
            columns={submissionManagementColumns}
            rows={gridRows.map(row => ({ id: row.submissionId, ...row }))}
            loading={isLoading}
            //pagination
            rowCount={getGridCount(gridRows.length, paginationModel)}
            disableRowSelectionOnClick
            pagination
            paginationModel={paginationModel}
            paginationMode="server"
            //filtering
            filterMode="server"
            disableMultipleRowSelection={false}
            checkboxSelection
            rowSelectionModel={visibleSelectedIds}
            onRowSelectionModelChange={handleRowSelectionChange}
            pinnedColumns={{ left: ['__check__'], right: ['actions'] }}
            hideFooter
            // Force all columns to render in test environment only
            {...(process.env.NODE_ENV === 'test' && {
              disableVirtualization: true,
            })}
            slots={{
              noResultsOverlay: NoRowsOverlay,
              noRowsOverlay: NoRowsOverlay,
            }}
            slotProps={{
              noResultsOverlay: { error: hasError } as Record<string, boolean>,
              noRowsOverlay: { error: hasError } as Record<string, boolean>,
            }}
          />

          <GridFooter
            // pagination
            page={paginationModel.page}
            rowsPerPage={paginationModel.pageSize}
            count={getGridCount(gridRows.length, paginationModel)}
            onPageChange={handlePaginationChange}
            onRowsPerPageChange={handlePageOptionChange}
            // selection
            selectedRows={selectedRows}
            actions={getSubmissionActions()}
            gridLevel={GridLevelName.SUBMISSION}
            getAmount={(row): number | null => row.submissionAmount ?? null}
          />
        </HHStack>
      </HHStack>
      <FloatingReturnButton
        isVisible={navigationState}
        navigateUrl={navigationState?.returnUrl}
        buttonText={navigationState?.returnButtonText}
      />
    </>
  );
};

export default SubmissionManagementGrid;
